import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { money, fmtDate } from "@/lib/constants";
import { useCamp } from "@/context/SettingsContext";
import PrintLogo from "@/components/print/PrintLogo";
import PrintFooter from "@/components/print/PrintFooter";
import PayQR from "@/components/print/PayQR";
import PayOnline from "@/components/print/PayOnline";
import { Button } from "@/components/ui/button";
import DemoStamp from "@/components/DemoStamp";

export default function PrintInvoice() {
  const { t, lang } = useLang();
  const { camp } = useCamp();
  const [params] = useSearchParams();
  const id = params.get("invoice");
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!inv) return;
    const timer = setTimeout(() => window.print(), 600);
    return () => clearTimeout(timer);
  }, [inv]);

  if (!inv) return <div className="p-8 text-sm text-muted-foreground">…</div>;

  const openRefs = (inv.open_refs || []).filter((o) => o.number !== inv.number);
  const openTotal = openRefs.reduce((s, o) => s + (o.total || 0), 0) + (inv.status === "paid" ? 0 : inv.total || 0);

  return (
    <div className="print-page bg-white text-slate-900 mx-auto max-w-[760px] p-10 print:p-0" data-testid="print-invoice">
      <DemoStamp />
      <div className="flex justify-end print:hidden mb-3">
        <Button size="sm" onClick={() => window.print()} data-testid="print-invoice-now">
          <Printer className="h-4 w-4 mr-1" />{t("print")}
        </Button>
      </div>

      <div className="flex items-start justify-between gap-6">
        <PrintLogo camp={camp} testid="print-invoice-logo" />
        <div className="text-right text-[11px] leading-relaxed text-slate-600">
          <p className="font-bold text-sm text-slate-900">{camp.camp_name}</p>
          {[camp.street, camp.postal_city, camp.country, camp.phone, camp.email, camp.website].filter(Boolean).map((l) => <p key={l}>{l}</p>)}
          {camp.vat_number && <p className="font-semibold text-slate-900">{camp.vat_number}</p>}
        </div>
      </div>

      <div className="mt-8 border-t border-slate-300 pt-5 flex justify-between text-sm">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("guest")}</p>
          <p className="font-bold text-base">{inv.guest_name}</p>
        </div>
        <table className="text-sm">
          <tbody>
            <tr><td className="pr-6 text-slate-500">{t("invoice")}</td><td className="font-bold text-right">{inv.number}</td></tr>
            <tr><td className="pr-6 text-slate-500">{t("date")}</td><td className="font-bold text-right">{fmtDate(inv.created_at)}</td></tr>
          </tbody>
        </table>
      </div>

      <table className="w-full mt-8 text-sm">
        <thead>
          <tr className="border-b border-slate-300 text-[10px] uppercase tracking-[0.14em] text-slate-500">
            <th className="text-left py-2">{t("description")}</th>
            <th className="text-right py-2 w-20">{t("qty")}</th>
            <th className="text-right py-2 w-24">{t("unit_price")}</th>
            <th className="text-right py-2 w-28">{t("total")}</th>
          </tr>
        </thead>
        <tbody>
          {inv.items.map((it, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-2">{it.description}</td>
              <td className="py-2 text-right">{it.qty}</td>
              <td className="py-2 text-right">{money(it.unit_price, inv.currency)}</td>
              <td className="py-2 text-right font-medium">{money(it.qty * it.unit_price, inv.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-5 ml-auto w-64 text-sm space-y-1">
        <p className="flex justify-between text-slate-600"><span>{t("subtotal")}</span><span>{money(inv.subtotal, inv.currency)}</span></p>
        <p className="flex justify-between text-slate-600"><span>{t("tax")} {inv.tax_rate}%</span><span>{money(inv.tax_amount, inv.currency)}</span></p>
        <p className="flex justify-between border-t border-slate-300 pt-2 font-display font-extrabold text-lg">
          <span>{t("total")}</span><span>{money(inv.total, inv.currency)}</span>
        </p>
        {inv.deposit_credit > 0 && (
          <>
            <p className="flex justify-between text-emerald-700" data-testid="print-deposit-credit">
              <span>{t("deposit_paid")}</span><span>−{money(inv.deposit_credit, inv.currency)}</span>
            </p>
            <p className="flex justify-between border-t border-slate-300 pt-2 font-display font-extrabold text-lg" data-testid="print-amount-due">
              <span>{t("amount_due")}</span><span>{money(inv.amount_due, inv.currency)}</span>
            </p>
          </>
        )}
      </div>

      {openRefs.length > 0 && (
        <div className="mt-6 text-[11px] text-slate-600" data-testid="print-open-refs">
          <p className="font-semibold">{t("settle_open_invoices")}</p>
          {openRefs.map((o) => (
            <p key={o.number}>{o.number} · {fmtDate(o.date)} · {money(o.total, inv.currency)}</p>
          ))}
          <p className="mt-1 font-bold text-slate-900" data-testid="print-open-total">
            {t("settle_total_open")}: {money(openTotal, inv.currency)}
          </p>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-start gap-10">
        <PayQR camp={camp} lang={lang} amount={inv.deposit_credit > 0 ? inv.amount_due : inv.total} reference={inv.number} />
        {inv.status !== "paid" && <PayOnline invoiceId={inv.id} lang={lang} />}
      </div>

      <PrintFooter camp={camp} lang={lang} vatLabel={t("setup_vat_number")} />
    </div>
  );
}
